// "Were you right about the enemy?" — the debrief's calibration panel.
//
// Every forecast the player made during the fight (`src/state/elicit.ts`), resolved against what the
// enemy actually did, beside the S-2 estimate's forecast for the same moment, on one reliability strip
// and with one Brier score each. Lower is better; 0.25 is what saying 50% to everything gets you, so
// the number has a meaning a player can hold onto.
//
// The plain-language line at the bottom is the part that does the teaching. "You were overconfident" is
// an opinion. "You said 70% three times, it happened once" is an arithmetic fact about their own
// numbers, and it is the only form of this feedback that survives an argument.
//
// Inserted in `DebriefStage.tsx` immediately under `<DecisionSupport ... />`:
//
//     <Calibration />

import { useMemo } from 'react';
import { useDebrief } from '../../state/debrief';
import { useElicit } from '../../state/elicit';
import { useStore } from '../../state/store';
import { plainLanguageRead, resolveAnswers, scoreboard } from '../../debrief/elicitScore';
import { calibrationStatus } from '../../ai/calibration';
import type { ReliabilityBin } from '../../ai/calibrationMath';

/** A Brier score's colour: better than coin-flipping is friendly, worse is a warning. */
function brierTone(brier: number): string {
  if (brier <= 0.15) return 'var(--color-friendly)';
  if (brier <= 0.25) return 'var(--color-paper)';
  return 'var(--color-warn)';
}

function Strip({ bins, label, color, testId }: {
  bins: ReliabilityBin[]; label: string; color: string; testId: string;
}) {
  const total = bins.reduce((s, b) => s + b.n, 0);
  return (
    <div data-testid={testId}>
      <div className="mono text-[10px] uppercase tracking-[0.12em] text-fog">{label}</div>
      <div className="mt-1 flex items-end gap-1" style={{ height: 52 }}>
        {bins.map((b) => {
          const height = b.n === 0 ? 2 : Math.max(4, Math.round(b.meanOutcome * 48));
          return (
            <div key={b.lo} className="flex flex-1 flex-col items-center gap-1">
              <span
                className="w-full rounded-sm"
                style={{ height, background: b.n === 0 ? 'var(--color-slate-700)' : color, opacity: b.n === 0 ? 0.4 : 1 }}
                title={
                  b.n === 0
                    ? `${Math.round(b.lo * 100)}-${Math.round(b.hi * 100)}%: nothing said in this band`
                    : `${Math.round(b.lo * 100)}-${Math.round(b.hi * 100)}%: said ${Math.round(b.meanPredicted * 100)}% `
                      + `${b.n} time${b.n === 1 ? '' : 's'}, happened ${Math.round(b.meanOutcome * 100)}% of the time`
                }
              />
              <span className="mono text-[8px] text-fog">{Math.round(b.hi * 100)}</span>
            </div>
          );
        })}
      </div>
      <div className="mono mt-0.5 text-[9px] text-fog">
        {total} forecast{total === 1 ? '' : 's'} · bar height is how often it actually happened
      </div>
    </div>
  );
}

export function Calibration() {
  const payload = useDebrief((s) => s.payload);
  const scrubToEvent = useDebrief((s) => s.scrubToEvent);
  const answers = useElicit((s) => s.answers);
  const scenario = useStore((s) => s.scenario);

  const resolved = useMemo(() => {
    if (!payload) return [];
    return resolveAnswers({
      answers,
      events: payload.events,
      truth: payload.truth,
      units: payload.snap.units,
      graphics: scenario?.graphics ?? [],
      objectiveId: scenario?.objectiveId,
    });
  }, [answers, payload, scenario]);

  const score = useMemo(() => scoreboard(resolved), [resolved]);
  const read = useMemo(() => plainLanguageRead(resolved), [resolved]);
  const calibration = calibrationStatus();

  const providerLabel = score.provider === 'jev'
    ? `Jev${score.calibrated ? `, calibrated on ${score.calibratedOnRuns.toLocaleString()} fights` : ', uncalibrated'}`
    : score.provider === 'mixed' ? 'heuristic and Jev'
      : 'heuristic';

  return (
    <section className="wt-card" data-testid="calibration-panel">
      <div className="flex flex-wrap items-baseline gap-2 border-b border-slate-700 pb-2">
        <span className="mono text-[11px] uppercase tracking-[0.2em] text-paper">Were you right about the enemy?</span>
        <span className="mono text-[10px] text-fog">
          {resolved.length} forecast{resolved.length === 1 ? '' : 's'} scored against what he actually did
        </span>
      </div>

      {resolved.length === 0 ? (
        <p className="mt-2 text-[12px] leading-relaxed text-fog">
          No forecasts to score. During a fight, at a decision point — contact in a named area of interest,
          the first sighting of an armoured contact, your lead element crossing a phase line — a card asks
          for one probability. Answer a few and this panel scores them against the enemy&apos;s real
          decisions, beside the S-2 estimate&apos;s answer for the same moment.
        </p>
      ) : (
        <>
          <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Strip bins={score.playerBins} label="You" color="var(--color-friendly)" testId="calibration-strip-player" />
            <Strip bins={score.estimatorBins} label={`S-2 estimate · ${providerLabel}`} color="var(--color-warn)" testId="calibration-strip-estimator" />
          </div>

          <div className="mono mt-3 flex flex-wrap items-baseline gap-4 text-[11px]">
            <span>
              <span className="text-fog">Your Brier score </span>
              <span style={{ color: brierTone(score.playerBrier) }} data-testid="calibration-player-brier">
                {score.playerBrier.toFixed(3)}
              </span>
            </span>
            <span>
              <span className="text-fog">S-2 estimate </span>
              <span style={{ color: brierTone(score.estimatorBrier) }} data-testid="calibration-estimator-brier">
                {score.estimatorBrier.toFixed(3)}
              </span>
            </span>
            <span className="text-fog">lower is better · 0.250 is saying 50% to everything</span>
          </div>

          <p className="mt-2 text-[12px] leading-relaxed text-paper" data-testid="calibration-read">{read}</p>

          <ul className="mt-2 space-y-1">
            {resolved.map((r, i) => (
              <li key={`${r.answer.t}-${i}`} className="text-[11px] leading-snug">
                <button
                  type="button"
                  onClick={() => scrubToEvent(r.answer.t)}
                  className="text-left text-fog underline decoration-dotted underline-offset-4 hover:text-[var(--color-friendly)]"
                  title="Scrub the replay to this moment"
                >
                  {r.line}
                </button>
                <span className="mono ml-2 text-[10px]" style={{ color: r.correct ? 'var(--color-friendly)' : 'var(--color-warn)' }}>
                  {r.correct ? 'right' : 'wrong'}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="mt-2 text-[10px] leading-snug text-fog">
        The S-2 estimate is a typed judgment with a probability, not prose — a named third, a contact role,
        a yes or no. {calibration.present
          ? `Its probabilities are recalibrated against ${calibration.runs.toLocaleString()} headless fights `
            + `(${calibration.samples.toLocaleString()} labelled pictures, fitted `
            + `${calibration.generatedAt ? new Date(calibration.generatedAt).toISOString().slice(0, 10) : 'recently'}).`
          : 'No calibration has been fitted yet, so its probabilities are raw — see docs/design/ai-patterns.md.'}
      </p>
    </section>
  );
}
